import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useCallback, useMemo, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { InventoryUntestedLotsDialog } from "../../../../components"
import { InventoryHoldArea } from "../../../../constants"
import { SHELVING_DEFAULT_FORM_VALUES } from "../../constants"
import { useAdjustmentLotSelection, useCreateSplitPalletsAdjustment } from "../../hooks"
import {
  type SplitPalletsAdjustmentFormValues,
  createAdjustmentQuantityToMoveSchema,
  splitPalletsAdjustmentFormSchema,
} from "../../schemas"
import { toSplitPalletsAdjustmentCreateInput } from "../../utils"
import { AdjustmentLotSelection } from "../AdjustmentLotSelection"
import { AdjustmentTypeHeader } from "../AdjustmentTypeHeader"

import { SplitPalletsAdjustmentFormDetails } from "./SplitPalletsAdjustmentFormDetails"
import { SplitPalletsAdjustmentFormLots } from "./SplitPalletsAdjustmentFormLots"

import { Button } from "@/components/common/Button"
import CancelDialog from "@/components/common/Modals/CancelDialog"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { InventoryAdjustmentType, type InventoryByProductWarehouse, type Product } from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

interface SplitPalletsAdjustmentFormDialogProps {
  product: Product
  warehouseAreaId: string
  warehouseInventories: InventoryByProductWarehouse[]
  warehouseOptions: Option<string>[]
  onClose: () => void
  onSuccess: () => void
}

export const SplitPalletsAdjustmentFormDialog: FC<SplitPalletsAdjustmentFormDialogProps> = ({
  product,
  warehouseAreaId,
  warehouseInventories,
  warehouseOptions,
  onClose,
  onSuccess,
}) => {
  const [quantityByLotId, setQuantityByLotId] = useState<Record<string, number | null>>({})
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)

  const form = useForm<SplitPalletsAdjustmentFormValues>({
    resolver: zodResolver(splitPalletsAdjustmentFormSchema),
    defaultValues: {
      warehouseAreaId,
      holdArea: InventoryHoldArea.Warehouse,
      shelving: SHELVING_DEFAULT_FORM_VALUES,
      comment: "",
      document: undefined,
    },
    mode: "onChange",
  })
  const {
    handleSubmit,
    setValue,
    watch,
    formState: { isValid, isSubmitting, errors },
  } = form
  const selectedWarehouseAreaId = watch("warehouseAreaId")

  const { createAdjustment } = useCreateSplitPalletsAdjustment()

  const selectedWarehouseArea = useMemo(
    () =>
      warehouseInventories.find((inventory) => inventory.warehouseArea?.warehouseAreaId === selectedWarehouseAreaId)
        ?.warehouseArea,
    [warehouseInventories, selectedWarehouseAreaId]
  )

  const {
    palletGroups,
    isFetching,
    selectedLotIds,
    selectedLots,
    untestedLots,
    sortBy,
    sortDirection,
    validateLotsTested,
    handleSearchChange,
    handleSortChange,
    handleSelectionChange,
    handleSelectionRemove,
    handleSelectionReset,
    handleUntestedLotsReset,
  } = useAdjustmentLotSelection({
    productExternalSystemId: product.externalSystemId!,
    warehouseAreaExternalSystemId: selectedWarehouseArea!.externalSystemId!,
  })

  const uomName = product.packageUsWeightUomDetails?.name ?? ""

  const canSubmit =
    isValid &&
    !isSubmitting &&
    !errors.document &&
    selectedLots.length > 0 &&
    selectedLots.every(
      (lot) =>
        createAdjustmentQuantityToMoveSchema({ lotQuantity: lot.totalUnits }).safeParse(
          quantityByLotId[lot.rawInventoryId]
        ).success
    )

  const handleQuantityChange = useCallback((lotId: string, value: number | null) => {
    setQuantityByLotId((prev) => ({ ...prev, [lotId]: value }))
  }, [])

  const handleLotRemove = useCallback(
    (lotId: string) => {
      handleSelectionRemove(lotId)
      setQuantityByLotId((prev) => {
        const next = { ...prev }
        delete next[lotId]
        return next
      })
    },
    [handleSelectionRemove]
  )

  const handleWarehouseChange = () => {
    handleSelectionReset()
    setQuantityByLotId({})
    setValue("holdArea", InventoryHoldArea.Warehouse, { shouldValidate: true })
    setValue("shelving", SHELVING_DEFAULT_FORM_VALUES, { shouldValidate: true })
  }

  const handleCancel = () => {
    setIsCancelConfirmationOpen(true)
  }

  const submitForm = async (formValues: SplitPalletsAdjustmentFormValues) => {
    if (!validateLotsTested()) return

    const isSuccess = await createAdjustment(
      toSplitPalletsAdjustmentCreateInput(formValues, Number(product.productId), quantityByLotId),
      formValues.comment,
      formValues.document
    )

    if (isSuccess) onSuccess()
  }

  if (isCancelConfirmationOpen) {
    return <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
  }

  if (untestedLots.length > 0) {
    return <InventoryUntestedLotsDialog untestedLots={untestedLots} onClose={handleUntestedLotsReset} />
  }

  return (
    <Dialog
      open
      fullWidth
      maxWidth={false}
      PaperProps={{ className: "h-full" }}
      onClose={handleCancel}
      data-testid='inventory.split-pallets-adjustment-form-dialog'
    >
      <DialogHeader title='Inventory Adjustment & Pallet Operations' onClose={handleCancel} />
      <FormProvider {...form}>
        <AdjustmentTypeHeader
          adjustmentType={InventoryAdjustmentType.SplitPallets}
          product={product}
          warehouseOptions={warehouseOptions}
          onWarehouseChange={handleWarehouseChange}
        />
        <DialogContent className='grid flex-1 grid-cols-[1fr_368px] gap-x-6 overflow-hidden px-10 pb-10 pt-6'>
          <Box className='flex min-w-0 flex-col gap-y-6 overflow-hidden'>
            <AdjustmentLotSelection
              palletGroups={palletGroups}
              isFetching={isFetching}
              selectedLotIds={selectedLotIds}
              sortBy={sortBy}
              sortDirection={sortDirection}
              uomName={uomName}
              onSelectionChange={handleSelectionChange}
              onSortChange={handleSortChange}
              onSearchChange={handleSearchChange}
            />
            <SplitPalletsAdjustmentFormLots
              selectedLots={selectedLots}
              uomName={uomName}
              quantityByLotId={quantityByLotId}
              onQuantityChange={handleQuantityChange}
              onLotRemove={handleLotRemove}
            />
          </Box>
          <SplitPalletsAdjustmentFormDetails warehouseArea={selectedWarehouseArea} />
        </DialogContent>
      </FormProvider>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button
          appearance='outlined'
          onClick={handleCancel}
          data-testid='inventory.split-pallets-adjustment-form-dialog.cancel-button'
        >
          Cancel
        </Button>
        <Button
          appearance='contained'
          disabled={!canSubmit}
          onClick={handleSubmit(submitForm)}
          data-testid='inventory.split-pallets-adjustment-form-dialog.submit-button'
        >
          Confirm Adjustment
        </Button>
      </DialogActions>
    </Dialog>
  )
}
